import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import Loading from "./Loading";
import { useRequest } from "../hooks/UseRequest";
import { setNotification } from "../redux/notificationSlice";
import { selectUser } from "../redux/userSlice";
import { hasUserPermission } from "../utils/permissionHelper";
import { handleToolTipMouseEnter } from "../helpers/layout";
import { buildQueryString } from '../helpers/formatting'
import { selectDropdownOptions, selectDropdowns } from "../redux/dropdownSlice";

export default function ViewSensitivePiiButtons(props) {
  const { customerId, type, style, documentId, documentTypeId = null, callback = null, loanId = null, noMarginLeft = false, disabled = false, bankAccountId = null, tooltipPosition = 'bottom' } = props
  const requests = useRequest()
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showReasonSelector, setShowReasonSelector] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [sensitiveDataResult, setSensitiveDataResult] = useState(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);
  const active = useSelector(selectUser)
  const activePermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options
  const hasPermission = (type === 'full_ssn' && hasUserPermission(activePermissions, 'can_view_full_ssn')) ||
    (type === 'full_bank_account_number' && hasUserPermission(activePermissions, 'can_view_full_bank_account_number')) ||
    (type === 'loan_agreement' && hasUserPermission(activePermissions, 'can_view_loan_agreement')) ||
    (type === 'bankruptcy_document' && hasUserPermission(activePermissions, 'can_view_bankruptcy_document')) ||
    (type === 'general_document' && hasUserPermission(activePermissions, 'can_view_general_document')) ||
    (type === 'business_license' && hasUserPermission(activePermissions, 'can_view_general_document'))
  const dropdownOptions = useSelector(selectDropdownOptions)
  const dropdowns = useSelector(selectDropdowns)
  const documentTypeDdo = documentTypeId ? dropdownOptions.filter((o) => o.id === documentTypeId)[0] : null

  const sensitivePiiViewReason = dropdowns.data?.find((o) => o.code_key === 'sensitive_pii_view_reason')
  const allReasonOptions = sensitivePiiViewReason
    ? dropdownOptions.filter((o) => o.dropdown_id === sensitivePiiViewReason.id).map((o) => ({
        label: o.name,
        value: o.id,
        optional_value_1: o.optional_value_1,
      }))
    : []
  const reasonOptions = allReasonOptions.filter((option) => {
    const tags = (option.optional_value_1 || '').toString().toLowerCase()
    if (type === 'full_ssn') return tags.includes('ssn')
    if (type === 'full_bank_account_number') return tags.includes('bank_account_num')
    if (type === 'general_document' || type === 'bankruptcy_document' || type === 'loan_agreement') return tags.includes('general_document')
    return false
  })

  useEffect(() => {
    if (!showReasonSelector) return
    const handleClickOutside = (e) => {
      if (
        wrapperRef.current && !wrapperRef.current.contains(e.target) &&
        popoverRef.current && !popoverRef.current.contains(e.target)
      ) {
        closePopover()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showReasonSelector])

  useEffect(() => {
    if (!showReasonSelector) return
    const handleScroll = () => closePopover()
    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [showReasonSelector])

  // A reveal that resolves after the popover closed, or after this button was re-pointed at another
  // record (the page reuses it when the agent moves to the next customer), must not surface later
  // without a fresh reason and audit entry — so every close or target change supersedes it.
  const requestId = useRef(0)

  const closePopover = () => {
    requestId.current += 1
    setIsLoading(false)
    setShowReasonSelector(false)
    setSelectedReason('')
    setSensitiveDataResult(null)
  }

  // Layout effect so a revealed value is cleared before the next paint, not after one frame on the new record.
  useLayoutEffect(() => {
    closePopover()
  }, [customerId, type, loanId, bankAccountId, documentId])

  // An in-flight reveal must not reach the parent's callback after this button unmounts.
  useEffect(() => () => { requestId.current += 1 }, [])

  const handleShowSensitiveData = async () => {
    if (isLoading) return true

    if (type === 'business_license') {
      await fetchSensitiveData()
      return
    }

    if ((type === 'full_ssn' || type === 'full_bank_account_number' || type === 'general_document' || type === 'bankruptcy_document' || type === 'loan_agreement') && typeof callback !== 'function') {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect()
        setPopoverPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX })
      }
      setShowReasonSelector(true)
      return
    }

    await fetchSensitiveData()
  }

  const handleReasonConfirm = async () => {
    if (!selectedReason) return
    await fetchSensitiveData(selectedReason)
  }

  const fetchSensitiveData = async (viewReasonId = null) => {
    const thisRequest = ++requestId.current
    try {
      setIsLoading(true);
      let params = {}
      if (loanId) params["loanId"] = loanId;
      if (bankAccountId) params["bankAccountId"] = bankAccountId;
      if (viewReasonId) params["sensitiveDataViewReasonId"] = viewReasonId;

      let url = type === 'business_license'
        ? `${process.env.REACT_APP_API_DOMAIN}/business_license_document/${documentId}`
        : `${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/${type}${documentId ? '/' + documentId : ''}`;
      url += buildQueryString(params)

      requests.setEndpoint(url)
      const response = await requests.get()
      if (thisRequest !== requestId.current) return null

      if (response.status === 200 || response.status === 201) {
        if (type === 'business_license') {
          window.open(response.data.data.s3_link, "_blank", "noreferrer");
          setIsLoading(false);
          return null;
        } else if (type === 'loan_agreement' || type === 'bankruptcy_document' || type === 'general_document') {
          window.open(response.data.data.sensitive_data, "_blank", "noreferrer");
          setIsLoading(false);
          closePopover();
          return null;
        } else {
          const sensitiveData = response.data.data.sensitive_data;
          if (typeof callback === 'function') {
            callback(sensitiveData);
            closePopover();
          } else {
            setSensitiveDataResult(sensitiveData);
          }
          setIsLoading(false);
          return sensitiveData;
        }
      }
    } catch (error) {
      if (thisRequest !== requestId.current) return null
      setIsLoading(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  let buttonLabel = 'View Full #'
  let buttonTooltip = 'This will show the full # and also log that you viewed it for this customer with the timestamp'
  if (type === 'loan_agreement') {
    buttonLabel = 'View'
    buttonTooltip = 'This will show the loan agreement and also log that you viewed it for this customer with the timestamp'
  } else if (type === 'bankruptcy_document') {
    buttonLabel = 'View'
    buttonTooltip = 'This will show the bankruptcy document and also log that you viewed it for this customer with the timestamp'
  } else if (type === 'general_document') {
    buttonLabel = 'View'
    buttonTooltip = `This will show the ${documentTypeDdo ? documentTypeDdo.name : ''} document and also log that you viewed it for this customer with the timestamp`
  } else if (type === 'business_license') {
    buttonLabel = 'View'
    buttonTooltip = 'This will show the business license document'
  }

  let dataTypeLabel = 'Full #'
  if (type === 'full_ssn') dataTypeLabel = 'Full SSN'
  else if (type === 'full_bank_account_number') dataTypeLabel = 'Full Bank Account Number'
  else if (type === 'general_document' || type === 'bankruptcy_document' || type === 'loan_agreement') dataTypeLabel = `${documentTypeDdo ? documentTypeDdo.name : ''} Document`
  else if (type === 'business_license') dataTypeLabel = 'Business License Document'

  return hasPermission && (
    <span ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className={`button has-background-grey has-text-white is-small mr-1 header-button ${noMarginLeft ? '' : 'ml-2'} pl-2 pr-2 disabled`}
        style={style}
        onClick={handleShowSensitiveData}
        disabled={disabled}
      >
        {isLoading ? (
          <Loading size="is-size-6" />
        ) : (
          <>
            {buttonLabel}
            <span
              className={`lox-no-border ml-1 lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-${tooltipPosition} has-tooltip-primary`}
              data-tooltip={buttonTooltip}
              onMouseEnter={(toolTip) => handleToolTipMouseEnter(toolTip, 'div.lox-hooktable')}
            >
              <FontAwesomeIcon
                icon={faQuestionCircle}
                className="has-text-link"
              />
            </span>
          </>
        )}
      </button>
      {showReasonSelector && ReactDOM.createPortal(
        <div
          ref={popoverRef}
          style={{
            position: 'absolute',
            top: popoverPos.top + 4,
            left: popoverPos.left,
            zIndex: 9999,
            background: 'white',
            border: '1px solid #dbdbdb',
            borderRadius: '4px',
            padding: '12px',
            minWidth: '280px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {sensitiveDataResult ? (
            <>
              <p className="is-size-7 has-text-weight-semibold mb-2">{dataTypeLabel}</p>
              <p className="has-text-centered mb-3">{sensitiveDataResult}</p>
              <div className="is-flex is-justify-content-flex-end">
                <button type="button" className="button is-small has-background-grey has-text-white" onClick={closePopover}>
                  Done
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="is-size-7 has-text-weight-semibold mb-2">Reason for viewing {dataTypeLabel}</p>
              <div className="select is-small is-fullwidth mb-2">
                <select value={selectedReason} onChange={(e) => setSelectedReason(e.target.value)}>
                  <option value="">Select a reason...</option>
                  {reasonOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="is-flex is-justify-content-flex-end" style={{ gap: '6px' }}>
                <button type="button" className="button is-small has-background-grey has-text-white" onClick={closePopover}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="button is-link is-small"
                  disabled={!selectedReason || isLoading}
                  onClick={handleReasonConfirm}
                >
                  {isLoading ? 'Loading...' : 'Show'}
                </button>
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </span>
  )
}
